import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { connect } from "react-redux";
import { backendLog } from "../../../util/logging";
import { logPageView } from "../../../util/analytics";
import { fetchExploreLists } from "../../../actions/savedSearch";
import FollowCityWidget from "./FollowCityWidget";
import { CenterDiv } from "../administration/SimplePage";
import ListCard from "../../common/ListCard";

// Static city data as fallback
const CITIES = {
  Arizona: [
    { name: "Litchfield Park", path: "/cities/arizona/litchfield_park" },
    { name: "Mesa", path: "/cities/arizona/mesa" },
    { name: "Phoenix", path: "/cities/arizona/phoenix" },
  ],
  California: [
    { name: "Oakland", path: "/cities/california/oakland" },
    { name: "San Francisco", path: "/cities/california/san_francisco" },
  ],
  Connecticut: [
    { name: "Hartford", path: "/cities/connecticut/hartford" },
    { name: "Middletown", path: "/cities/connecticut/middletown" },
    { name: "New Haven", path: "/cities/connecticut/new_haven" },
    { name: "Wallingford", path: "/cities/connecticut/wallingford" },
  ],
  Massachusetts: [
    { name: "Boston", path: "/cities/massachusetts/boston" },
    { name: "Cape Cod", path: "/cities/massachusetts/cape_cod?zoom=10" },
    { name: "East Weymouth", path: "/cities/massachusetts/east_weymouth" },
    {
      name: "Green Harbor-Cedar Crest",
      path: "/cities/massachusetts/green_harbor-cedar_crest",
    },
    { name: "Salem", path: "/cities/massachusetts/salem" },
    { name: "Sharon", path: "/cities/massachusetts/sharon" },
    { name: "Somerville", path: "/cities/massachusetts/somerville" },
    { name: "Springfield", path: "/cities/massachusetts/springfield" },
    { name: "Weymouth", path: "/cities/massachusetts/weymouth" },
    { name: "Whitinsville", path: "/cities/massachusetts/whitinsville" },
    { name: "Worcester", path: "/cities/massachusetts/worcester" },
  ],
  "New Hampshire": [
    { name: "Concord", path: "/cities/new_hampshire/concord" },
    { name: "Manchester", path: "/cities/new_hampshire/manchester" },
    { name: "Nashua", path: "/cities/new_hampshire/nashua" },
  ],
  "New Jersey": [
    { name: "Hoboken", path: "/cities/new_jersey/hoboken" },
    { name: "Ridgewood", path: "/cities/new_jersey/ridgewood" },
  ],
  "New York": [
    { name: "New York City", path: "/cities/new_york/new_york" },
    { name: "Buffalo", path: "/cities/new_york/buffalo" },
    { name: "Syracuse", path: "/cities/new_york/syracuse" },
  ],
  Pennsylvania: [
    { name: "Danville", path: "/cities/pennsylvania/danville" },
  ],
  "Rhode Island": [
    { name: "Providence", path: "/cities/rhode_island/providence" },
  ],
  Virginia: [
    { name: "Arlington", path: "/cities/virginia/arlington" },
  ],
  "Washington, DC": [
    {
      name: "Washington, DC",
      path: "/cities/district_of_columbia/washington",
    },
  ],
};

const SavedSearchCard = ({ list }) => {
  const displayCity = list.state
    ? `${list.cityName}, ${list.state}`
    : list.cityName;
  return (
    <ListCard
      to={`/lists/${list.slug}`}
      title={list.name}
      description={list.seoMetaDescription || list.description}
      cityLabel={displayCity}
    />
  );
};

// Group lists by state, then by city within each state
function groupListsByLocation(lists) {
  const grouped = {};
  for (const list of lists) {
    const state = list.state || "Other";
    const city = list.cityName || "Unknown";
    if (!grouped[state]) grouped[state] = {};
    if (!grouped[state][city]) grouped[state][city] = [];
    grouped[state][city].push(list);
  }
  // Sort states alphabetically, cities alphabetically within each state
  const sorted = {};
  for (const state of Object.keys(grouped).sort()) {
    sorted[state] = {};
    for (const city of Object.keys(grouped[state]).sort()) {
      sorted[state][city] = grouped[state][city];
    }
  }
  return sorted;
}

const Explore = ({ token, ...props }) => {
  const [allLists, setAllLists] = useState([]);
  const [nearbyLists, setNearbyLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingNearby, setLoadingNearby] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [expandedStates, setExpandedStates] = useState({});
  const [dynamicCities, setDynamicCities] = useState(null);

  useEffect(function logPageViewOnRender() {
    backendLog("User visited the explore page");
    logPageView();
  });

  // Fetch all public lists
  useEffect(function fetchPublicListsOnMount() {
    const load = async () => {
      try {
        const lists = await fetchExploreLists({});
        setAllLists(lists);
      } catch (err) {
        console.error("Failed to load lists:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Fetch dynamic cities from API
  useEffect(function fetchDynamicCitiesOnMount() {
    const loadCities = async () => {
      try {
        const res = await fetch("/api/cities");
        if (res.ok) {
          const cities = await res.json();
          if (cities.length > 0) {
            // Group by state
            const grouped = {};
            for (const city of cities) {
              const state = city.state || "Other";
              if (!grouped[state]) grouped[state] = [];
              grouped[state].push(city);
            }
            setDynamicCities(grouped);
          }
        }
      } catch (err) {
        console.error("Failed to load dynamic cities:", err);
      }
    };
    loadCities();
  }, []);

  // Try to get user location for nearby lists
  useEffect(function getUserLocationOnMount() {
    if (!navigator.geolocation) return;
    setLoadingNearby(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
      },
      () => {
        setLoadingNearby(false);
      },
      { timeout: 5000 }
    );
  }, []);

  // Fetch nearby lists when we have user location
  useEffect(function fetchNearbyListsWhenLocationAvailable() {
    if (!userLocation) return;
    const load = async () => {
      try {
        const lists = await fetchExploreLists({
          lat: userLocation.lat,
          lng: userLocation.lng,
          radius: 100,
          limit: 10,
        });
        setNearbyLists(lists);
      } catch (err) {
        console.error("Failed to load nearby lists:", err);
      } finally {
        setLoadingNearby(false);
      }
    };
    load();
  }, [userLocation]);

  // Featured = 5 most recent public lists
  const featuredLists = useMemo(() => allLists.slice(0, 5), [allLists]);

  // Group all lists by state → city
  const groupedLists = useMemo(() => groupListsByLocation(allLists), [allLists]);

  const toggleState = (state) => {
    setExpandedStates((prev) => ({ ...prev, [state]: !prev[state] }));
  };

  return (
    <div
      style={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Helmet>
        <title>VegifyIt | Explore</title>
        <meta
          name="description"
          content="Discover the towns and metros that have entries in VegifyIt. Use this page to help find your next vegan-friendly dish or restaurant near or far!"
        />
      </Helmet>
      <CenterDiv>
        <h2>Explore</h2>

        {/* Nearby Lists Section */}
        {(loadingNearby || nearbyLists.length > 0) && (
          <section style={{ marginBottom: "24px" }}>
            <h3>
              <i className="fas fa-location-arrow" /> Lists Near You
            </h3>
            {loadingNearby ? (
              <p style={{ color: "#888" }}>
                <i className="fas fa-spinner fa-spin" /> Finding lists near
                you...
              </p>
            ) : (
              nearbyLists.map((list) => (
                <SavedSearchCard key={list._id} list={list} />
              ))
            )}
          </section>
        )}

        {/* Featured Lists Section */}
        {(loading || featuredLists.length > 0) && (
          <section style={{ marginBottom: "24px" }}>
            <h3>
              <i className="fas fa-star" /> Featured Lists
            </h3>
            {loading ? (
              <p style={{ color: "#888" }}>
                <i className="fas fa-spinner fa-spin" /> Loading lists...
              </p>
            ) : (
              featuredLists.map((list) => (
                <SavedSearchCard key={list._id} list={list} />
              ))
            )}
          </section>
        )}

        {/* Browse by Location — grouped lists */}
        {Object.keys(groupedLists).length > 0 && (
          <section style={{ marginBottom: "24px" }}>
            <h3>
              <i className="fas fa-map" /> Browse by Location
            </h3>
            {Object.entries(groupedLists).map(([state, cities]) => {
              const isExpanded = expandedStates[state];
              const listCount = Object.values(cities).reduce(
                (sum, lists) => sum + lists.length,
                0
              );
              return (
                <div key={state} style={{ marginBottom: "4px" }}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleState(state)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleState(state);
                      }
                    }}
                    style={{
                      padding: "8px 12px",
                      background: "#f5f5f5",
                      borderRadius: "6px",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      userSelect: "none",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      <i
                        className={`fas fa-chevron-${isExpanded ? "down" : "right"}`}
                        style={{
                          marginRight: "8px",
                          fontSize: "0.8em",
                          width: "10px",
                        }}
                      />
                      {state}
                    </span>
                    <span
                      style={{ color: "#888", fontSize: "0.85em" }}
                    >
                      {listCount} list{listCount !== 1 ? "s" : ""}
                    </span>
                  </div>
                  {isExpanded && (
                    <div style={{ paddingLeft: "12px", marginTop: "4px" }}>
                      {Object.entries(cities).map(([city, lists]) => (
                        <div key={city} style={{ marginBottom: "8px" }}>
                          <h4
                            style={{
                              margin: "8px 0 4px",
                              fontSize: "1em",
                              color: "#444",
                            }}
                          >
                            {city}
                          </h4>
                          {lists.map((list) => (
                            <SavedSearchCard key={list._id} list={list} />
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* Browse by City — dynamic with static fallback */}
        <section>
          <h3>
            <i className="fas fa-city" /> Browse by City
          </h3>
          <p>
            If VegifyIt doesn't have restaurants in your area yet, feel free to
            explore some of our supported cities!
          </p>
          {dynamicCities
            ? Object.entries(dynamicCities)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([state, cities]) => (
                  <div key={state}>
                    <h4>{state}</h4>
                    <ul>
                      {cities.map((city) => (
                        <p key={city.path}>
                          <a href={city.path}>
                            {city.cityName} ({city.restaurantCount}{" "}
                            restaurant
                            {city.restaurantCount !== 1 ? "s" : ""})
                          </a>
                        </p>
                      ))}
                    </ul>
                  </div>
                ))
            : Object.entries(CITIES).map(([state, cities]) => (
                <div key={state}>
                  <h4>{state}</h4>
                  <ul>
                    {cities.map((city) => (
                      <p key={city.path}>
                        <a href={city.path}>{city.name}</a>
                      </p>
                    ))}
                  </ul>
                </div>
              ))}
        </section>

        <div style={{ border: "2px solid black", padding: "16px" }}>
          <h3>
            <i className="fas fa-bell" /> Follow a City's Vegan Scene
          </h3>
          <p>
            Get notified when new vegan restaurants and dishes are added near
            you. Pick a city and we'll keep you in the loop!
          </p>
          <FollowCityWidget />
        </div>
      </CenterDiv>
    </div>
  );
};

const mapStateToProps = (state) => ({
  token: state.auth.token,
});
export default connect(mapStateToProps, {})(Explore);
